import { useState } from "react";
import { documentsService } from "../services/documents";
import { ApiError } from "../services/api";
import { Button } from "./ui/Button";
import type { DocumentType, DocumentVersion } from "../types";

interface Props {
  jobId: string;
  docType: DocumentType;
  onNewVersion: (version: DocumentVersion) => void;
  isGenerating?: boolean;
}

export default function ChatPanel({ jobId, docType, onNewVersion, isGenerating = false }: Props) {
  const [instruction, setInstruction] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastInstruction, setLastInstruction] = useState<string | null>(null);

  const isLocked = isGenerating || sending;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = instruction.trim();
    if (!trimmed) return;
    setSending(true);
    setError(null);
    try {
      const version = await documentsService.chat(jobId, docType, trimmed);
      setLastInstruction(trimmed);
      setInstruction("");
      onNewVersion(version);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.detail);
      } else {
        setError("Failed to refine document. Please try again.");
      }
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-slate-500 dark:text-slate-400 text-sm m-0">
        Describe how you'd like to improve the document. The AI will apply your instruction and create a new version.
      </p>

      {lastInstruction && (
        <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-md px-3.5 py-2.5 text-sm text-green-800 dark:text-green-200">
          ✓ Applied: "{lastInstruction}"
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md px-3.5 py-2.5 text-sm text-error">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <textarea
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          rows={4}
          placeholder="e.g. Make the summary more concise. Highlight Python experience in the skills section."
          disabled={isLocked}
          readOnly={isGenerating}
          className={[
            "w-full px-3 py-2.5 border border-slate-300 dark:border-slate-600 rounded-md text-sm",
            "bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100",
            "placeholder-slate-400 resize-vertical font-inherit",
            "focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent",
            "transition-all duration-200",
            isLocked ? "opacity-50 cursor-not-allowed" : "",
          ].filter(Boolean).join(" ")}
        />
        <Button
          type="submit"
          disabled={isLocked || !instruction.trim()}
          loading={sending}
          variant="primary"
          size="sm"
          className="self-start"
        >
          {sending ? "Refining…" : "Apply Instruction"}
        </Button>
      </form>
    </div>
  );
}
